/* eslint-disable no-plusplus */
import moment from 'moment';
import timezoneMoment from 'moment-timezone';
import jp from 'jsonpath';
import _ from 'lodash';

export default function formatNumberTwoDecimals(inputNumber) {
  return parseFloat(inputNumber).toFixed(2);
}

export function toCentralTime(dateTime) {
  return timezoneMoment.tz(dateTime, 'America/Chicago');
}

export function toCentralTimeDisplayDate(dateTime) {
  return toCentralTime(dateTime).format('YYYY-MM-DD');
}

export function formatMilitaryTime(inputDateTime) {
  return moment(inputDateTime).format('HH:mm');
}

export function formatTime(inputDateTime) {
  return toCentralTime(inputDateTime).format('hh:mm A');
}

export function formatDateAbvMonthDayFourDigitYear(inputDateTime) {
  return moment(inputDateTime).format('MM-DD-YY');
}

export function formatDayOfWeekAbv(inputDay) {
  return moment(inputDay).format('ddd');
}

export function reverseArrayOrder(inputArray) {
  const reversedArray = [];
  for (let i = inputArray.length - 1; i >= 0; i--) {
    reversedArray.push(inputArray[i]);
  }
  return reversedArray;
}

export function formatMonthDay(inputDateTime) {
  return moment(inputDateTime).format('MM/DD');
}

export function severityOfHours(inputHours) {
  if (inputHours < 32) { return 'app-good'; }
  if (inputHours > 32 && inputHours < 40) { return 'app-warning'; }

  return 'app-danger';
}

export function getQueryStringValue(key) {
  return decodeURIComponent(window.location.search.replace(new RegExp(`^(?:.*[&\\?]${encodeURIComponent(key).replace(/[.+*]/g, '\\$&')}(?:\\=([^&]*))?)?.*$`, 'i'), '$1'));
}

export function defaultTableFiltering(filter, row) {
  const rowValue = String(row[filter.id]).toLocaleLowerCase();
  const filterValue = String(filter.value).toLocaleLowerCase();
  return (rowValue.includes(filterValue));
}

export function startsWithTableFiltering(filter, row) {
  const rowValue = String(row[filter.id]).toLocaleLowerCase();
  const filterValue = String(filter.value).toLocaleLowerCase();
  return (rowValue.startsWith(filterValue));
}

export function tableStringSorting(objectA, objectB, field) {
  if (objectA[field] > objectB[field]) { return -1; }
  if (objectA[field] < objectB[field]) { return 1; }

  return 0;
}

export function dateTableSorting(objectA, objectB) {
  if (!objectA && objectB) {
    return -1;
  }
  if (objectA && !objectB) {
    return 1;
  }
  if (objectA && objectB) {
    const unixDateA = moment(objectA.iso8601).unix();
    const unixDateB = moment(objectB.iso8601).unix();

    if (unixDateA > unixDateB) { return -1; }
    if (unixDateA < unixDateB) { return 1; }
  }
  return 0;
}

export function dateTableFiltering(dateFilter, row) {
  const rowValue = String(row[dateFilter.id].display);
  const filterValue = String(dateFilter.value);
  return (rowValue.includes(filterValue));
}

export function toDollarFormat(input) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });

  return formatter.format(input);
}

export function setLoginHeaders(responseHeaders) {
  /* eslint-disable quote-props, dot-notation */
  const credentials = {
    'access-token': responseHeaders['access-token'],
    'uid': responseHeaders['uid'],
    'expiry': responseHeaders['expiry'],
    'client': responseHeaders['client'],
    'token-type': responseHeaders['token-type'],
  };
  /* eslint-enable quote-props, dot-notation */

  localStorage.setItem('credentials', JSON.stringify(credentials));
}

export function getResetHeaders() {
  const headerObj = {};
  headerObj.uid = getQueryStringValue('uid');
  headerObj['access-token'] = getQueryStringValue('token');
  headerObj.client = getQueryStringValue('client_id');

  return headerObj;
}

/* eslint-disable no-plusplus */
export function parseErrors(e) {
  let errorMessage = '';
  if (e.graphQLErrors) {
    const errorObj = e.graphQLErrors[0].problems;
    if (errorObj) {
      for (let i = 0; i < errorObj.length; i++) {
        errorMessage += errorObj[i].explanation;
        errorMessage += ' for';
        for (let j = 0; j < errorObj[i].path.length; j++) {
          errorMessage += (j > 0) ? ' and ' : ' ';
          errorMessage += errorObj[i].path[j];
        }
      }
    } else {
      errorMessage = e.graphQLErrors[0].details;
    }
    return errorMessage;
  }
  return 'Invalid error response.';
}
/* eslint-enable no-plusplus */
/**
* querySelector makes it easier to destructure query results in to values that
* are convenient to use as props.
*/
export const querySelector = (queryResult, selectorPath, defaultValue = undefined) => {
  if (queryResult.loading || queryResult.error) {
    return defaultValue;
  }
  try {
    const selection = jp.query(queryResult, selectorPath);
    return selection;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error: querySelector', e);
    return defaultValue;
  }
};

/**
* Wraps ES6 Promise in a deferred-like interface. It is often handy to separate
* the flow of the promise and resolve/reject callbacks.
*/
export const deferred = () => {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
};


/**
 * Given an array of nodes with IDs and the current ID, return the next ID.
 * Assumes that collection items are not being replaced in the collection.
 */
export const nextId = (collection, currentId) => {
  const currentIndex = _.findIndex(collection, { id: currentId });
  const collectionLength = collection.length;
  const isLast = (currentIndex + 1) === collectionLength;
  const isOnly = collectionLength === 1;

  if (isOnly) {
    return null;
  }

  if (isLast) {
    const nextNode = collection[currentIndex - 1];
    return nextNode.id;
  }

  const nextNode = collection[currentIndex + 1];
  return nextNode.id;
};


/**
 * Given an array of nodes with IDs and the current ID, return the next ID.
 * Assumes that collection items are being replace in the collection.
 */
export const nextIdWithReplacement = (collection, currentId) => {
  const currentIndex = _.findIndex(collection, { id: currentId });
  const collectionLength = collection.length;
  const isLast = (currentIndex + 1) === collectionLength;

  if (isLast) {
    return currentId;
  }

  const nextNode = collection[currentIndex + 1];
  return nextNode.id;
};

export const sleep = (ms) => {
  return new Promise((resolve) => {
    return (
      setTimeout(resolve, ms)
    );
  });
};

export const remapKeys = (object, mapping) => {
  return _.mapKeys(object, (value, key) => {
    return mapping[key];
  });
};

export const openInNewTab = (url) => {
  const win = window.open(url, '_blank');
  if (win) {
    win.focus();
  }
};

export const maybeId = (item) => {
  if (item) {
    return item.id;
  }
  return null;
};
